/**
 * The signed-in browser session, or none.
 *
 * **The seam that keeps MSAL out of everything else.** `lib/api.ts` needs a
 * bearer token and `App.tsx` needs an address and a way out; neither imports
 * MSAL, and neither knows it exists. Each app's gate registers a session once,
 * on the way through, and its `App` mounts only after it has (ADR-0034 — it was
 * the bootstrap before `createRoot` under ADR-0027).
 *
 * Module-level state rather than a React context, on the same reasoning
 * `mockPersona.ts` already carries and `apps/web/CLAUDE.md` states outright: the
 * value is written once, before anything that reads it exists, and never
 * changes — so a provider would be an abstraction bought before it is needed.
 * Nothing re-renders when it changes because nothing changes it. The gate is
 * upstream of every reader, which is what keeps that true now that the writer
 * is itself a component.
 *
 * **One module, not a token-only one.** The email, the token, and the sign-out
 * are one fact — *this tab is signed in as somebody* — and splitting them would
 * let the header believe there is a session while `api.ts` believes there is
 * not. Same rule as `RECYCLE_BIN_DAYS`: one reader per fact, and one writer.
 *
 * Under `VITE_USE_MOCKS=true` nothing ever registers one, which is what keeps
 * the whole mocked path — and the whole test suite — free of Entra (Article 5).
 */
export interface SignedInSession {
  /**
   * The account's address — from the token on the bootstrap path.
   *
   * Deliberately not `me.user.email` there: it has to render in the states where
   * `/me` failed, including the 403 where an authenticated account is simply
   * not provisioned here and the only useful act left is signing out.
   *
   * A cookie session has no token to read one off and is registered only after
   * `/me` has answered, so on that path this *is* `me.user.email`. The readers
   * are unchanged either way, which is the point of the seam.
   */
  readonly email: string;

  /**
   * The account's name, from the token's `name` claim.
   *
   * Same reasoning as `email` directly above, and one addition: the sign-in
   * gate greets the account *before* `GET /me` has answered (ADR-0034), so a
   * name that costs a round trip could not be used at all.
   *
   * `AccountInfo.name` is optional in MSAL and is absent for some B2B guest
   * shapes, so `msal.ts` falls back to the address. This is a string, never
   * `undefined` — the fallback belongs at the one place that knows the account,
   * not at every reader.
   */
  readonly displayName: string;

  /**
   * A valid access token for the API, refreshing silently if needed.
   *
   * Called per request rather than once, because it is the thing that knows
   * whether the cached token has expired.
   *
   * **Absent when the browser holds the session as a cookie** (ADR-0045). There
   * is no token to acquire on that path and there must not be a header: the
   * cookie *is* the credential, `fetch` sends it on its own, and a `Bearer` with
   * nothing behind it is worse than no header at all. Optional here rather than
   * a second session type, because every other field means the same thing on
   * both paths and `http.ts` is the one reader that has to care.
   */
  readonly getAccessToken?: (() => Promise<string>) | undefined;

  /**
   * Ends the session and returns to the origin. Never resolves usefully.
   *
   * What it ends depends on what there is: the Entra session on the bootstrap
   * path, and the Blue Partner row alone on the cookie path, where no Entra
   * session was ever created in this tab.
   */
  readonly signOut: () => void;
}

let current: SignedInSession | undefined;

export function setSession(session: SignedInSession): void {
  current = session;
}

export function getSession(): SignedInSession | undefined {
  return current;
}

/** Drop the registered session. Tests only — called from `test/setup.ts`. */
export function resetSession(): void {
  current = undefined;
}
